import React, { useState, useEffect } from "react";
import CheckBox from "~/Common-Objects/UI/Forms/CheckBox/CheckBox";
import Icons from "~/Common-Objects/UI/Icons/index";
import SelectNew from "~/Common-Objects/UI/Select";
import Toggle from "~/Common-Objects/UI/Forms/Toggle/Toggle";
import Dialog from "../../../components/dialog";
import Button from "../../../components/button";
import TextInput from "../../../components/textInput";
import * as styles from "./index.module.scss";
import i18n from '~/i18n'
const types = ['text', 'textarea', 'select', 'number', 'date', 'boolean', 'url']

export default function AddEditFieldDialog({
	fieldToEdit,
	isOpen,
	onSubmit,
	...rest
}) {
	const [name, setName] = useState(fieldToEdit ? fieldToEdit.name : "");
	const [type, setType] = useState(fieldToEdit ? fieldToEdit.type : "text");
	const [options, setOptions] = useState(fieldToEdit ? fieldToEdit.options : []);
	const [agentSpecific, setAgentSpecific] = useState(fieldToEdit ? fieldToEdit.agentSpecific : false);
	const [required, setRequired] = useState(fieldToEdit ? fieldToEdit.required : false);
	const [submitting, setSubmitting] = useState(false);
	const [formErrors, setFormErrors] = useState(null);

	useEffect(() => {
		if (isOpen) {
			if (fieldToEdit) {
				setName(fieldToEdit.name);
				setType(fieldToEdit.type);
				setOptions(fieldToEdit.options);
				setAgentSpecific(fieldToEdit.agentSpecific);
				setRequired(fieldToEdit.required);
			} else {
				setName("");
				setType("text");
				setOptions([]);
				setAgentSpecific(false);
				setRequired(false);
			}
		}
	}, [isOpen]);

	async function handleSubmit() {
		setSubmitting(true);

		try {
			const hasErrors = await validateForm();
			if (hasErrors) return;
			await onSubmit(name, type, options, agentSpecific, required);
		} finally {
			setSubmitting(false);
		}
	}

	async function validateForm() {
		const errors = {}
		let hasErrors = false

		if (!name || name.trim().length < 3) {
			hasErrors = true
			errors.name = i18n.t('chat:settings.fields.errors.nameMinLength')
		}

		if (type === 'select' && !options?.length) {
			hasErrors = true
			errors.options = i18n.t('chat:settings.fields.errors.selectNeedsOptions')
		} else if (type === 'select') {
			if (options.some(o => !o.value || !o.value.trim())) {
				hasErrors = true
				errors.options = i18n.t('chat:settings.fields.errors.allOptionsNeedValue')
			}

			const values = options.map(o => o.value)
			if (Array.from(new Set(values)).length !== values.length) {
				hasErrors = true
				errors.options = i18n.t('chat:settings.fields.errors.optionsMustBeUnique')
			}
		}

		if (hasErrors) {
			setFormErrors(errors)
		} else {
			setFormErrors(null)
		}
		return hasErrors
	}

	return (
		<Dialog
			fullScreen
			open={isOpen}
			title={fieldToEdit ? i18n.t('chat:settings.fields.editTitle') : i18n.t('chat:settings.fields.addTitle')}
			description={
				fieldToEdit
					? i18n.t('chat:settings.fields.editDesc')
					: i18n.t('chat:settings.fields.addDesc')
			}
			submitting={submitting}
			{...rest}
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					handleSubmit();
				}}
			>


				<TextInput
					label={i18n.t('chat:settings.fields.fieldName')}
					value={name}
					aria-label={i18n.t('chat:settings.fields.fieldName')}
					onChange={e => setName(e.target.value)}
					error={formErrors?.name}
					className="mar-b24"
				/>

				<SelectNew.Root
					className="mar-b24"
					value={type}
					error={formErrors?.type}
					onValueChange={(val) => setType(val)}
				>
					<SelectNew.Trigger
						fullWidth
						label={i18n.t('chat:settings.fields.fieldType')}
						placeholder={i18n.t('chat:settings.fields.selectType')}
						disabled={!!fieldToEdit}
					/>

					<SelectNew.Content fullWidth>
						<SelectNew.Viewport>
							{types
								.map((type) => {
									return (
										<SelectNew.Item
											key={type}
											value={type}
										>
											{i18n.t("chat:settings.fields.types." + type)}
										</SelectNew.Item>
									)
								})}
						</SelectNew.Viewport>
					</SelectNew.Content>
				</SelectNew.Root>

				{type === 'select' && (
					<Options options={options} setOptions={setOptions} formErrors={formErrors} />
				)}

				<Toggle
					after={i18n.t('chat:settings.fields.agentSpecific')}
					afterSecondary={i18n.t('chat:settings.fields.agentSpecificDesc')}
					checked={agentSpecific}
					onChange={() => setAgentSpecific(r => !r)}
					outerClassName="mar-b24" />

				{/* <CheckBox
          checked={required}
          onChange={() => setRequired(r => !r)}
          outerClassName="mar-b24"
        >
          Required before closing chat
        </CheckBox> */}

				<div className={styles.footer}>
					<Button type="submit" size="s" loading={submitting}>
						{fieldToEdit ? i18n.t('chat:settings.fields.saveFieldButton') : i18n.t('chat:settings.fields.addField')}
					</Button>
				</div>
			</form>
		</Dialog>
	);
}


function Options({ options, formErrors, setOptions }) {
	const [draggedIndex, setDraggedIndex] = useState(null);
	const [dragging, setDragging] = useState(false);
	const [touched, setTouched] = useState({})

	const handleDragStart = (index) => {
		setDraggedIndex(index);
		setDragging(true);
	};

	const handleDragOver = (index) => {
		if (draggedIndex === index) return;

		const newOptions = [...options];
		const [draggedItem] = newOptions.splice(draggedIndex, 1);
		newOptions.splice(index, 0, draggedItem);

		setDraggedIndex(index);
		setOptions(newOptions);
	};

	const handleDrop = () => {
		setDraggedIndex(null);
		setDragging(false);
	};

	const addOption = () => {
		setOptions([...options, { label: '', value: '' }])
	}

	const editValue = (i, e) => {
		const rows = [...options]
		const row = rows[i]
		const name = e.target.name
		const value = e.target.value
		if (name === 'label' && !touched[i + 'value'] && (!row.value?.length || row.value === row.label)) row.value = value
		if (name === 'value' && !touched[i + 'label'] && (!row.label?.length || row.label === row.value)) row.label = value
		row[name] = value
		setOptions(rows)
	}

	return (
		<>
			<label className="mar-b8" style={{ display: 'block' }}>{i18n.t('chat:settings.fields.optionsLabel')}</label>
			{
				(options || []).map((row, i) => <div
					className="quickFlex mar-b8"
					style={{
						gap: 8,
						alignItems: i === 0 ? 'flex-end' : 'center',
						backgroundColor: dragging && draggedIndex === i ? '#f0f0f0' : 'transparent',
						border: dragging && draggedIndex === i ? '1px dashed #ccc' : 'none'
					}}
					key={i}
					onDragOver={(e) => {
						e.preventDefault();
						handleDragOver(i);
					}}
					onDrop={handleDrop}
				>
					<div
						style={{ cursor: 'grab' }}
						draggable
						onDragStart={() => handleDragStart(i)}
						onDragEnd={handleDrop}
					>
						<Icons icon="drag" color="var(--neutral-600)" style={{ display: 'flex', alignItems: 'center', marginBottom: i === 0 ? 4 : 0 }} />
					</div>
					{/* <TextInput
						label={!i && "Label"}
						value={row.label}
						aria-label="Label"
						name="label"
						onClick={() => setTouched({ ...touched, [i + 'label']: true })}
						outerStyle={{ flexGrow: 1 }}
						onChange={e => {
							editValue(i, e)
						}}
					/> */}
					<TextInput
						// label={!i && "Value"}
						value={row.value}
						aria-label={i18n.t('chat:settings.fields.valueAriaLabel')}
						name="value"
						onClick={() => setTouched({ ...touched, [i + 'value']: true })}
						outerStyle={{ flexGrow: 1 }}
						onChange={e => {
							editValue(i, e)
						}}
					/>
					{options.length > 1 && <Button size="s" onClick={() => {
						const newOptions = [...options]
						newOptions.splice(i, 1)
						setOptions(newOptions)
					}}>{i18n.t('chat:settings.fields.remove')}</Button>}
				</div>)
			}
			<Button size="s" variant="secondary" className={formErrors?.options ? "mar-b8 mar-t20" : "mar-b24 mar-t20"} onClick={addOption}>{i18n.t('chat:settings.fields.addOption')}</Button >
			{formErrors?.options && <div className="mar-b24" style={{ color: 'red' }}>{formErrors.options}</div>
			}
		</>
	)
}